/**
 * modals/InstrumentSelectionModal.tsx
 * Instrument selection modal for adding instruments to fee configuration
 * 
 * PHASE 2: Modal shell with simplified instrument selection patterns
 * Simplified version of ReferenceCatalogTab patterns with search, filter, and multi-selection
 * Import/Cancel buttons (non-functional)
 */

// 1. External dependencies
import React, { useState, useMemo, useEffect } from 'react';
import { Search, Plus, Package, CheckSquare } from 'lucide-react';

// 2. Internal absolute imports
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Select, SelectItem, Chip, useDisclosure, Listbox, ListboxItem, ListboxSection, Accordion, AccordionItem } from '@enfomentis/ui';
import { InstrumentModal } from '@/components/shared/InstrumentExplorer/InstrumentModal';
import type { AssetClassType, MarketSegment, SubAssetClass } from '@/types/shared/AssetClass';
import { ASSET_CLASS_TYPE_DISPLAY_MAP } from '@/constants/assetClass';
import { formatAssetClassTypeDisplay } from '@/utils/assetClass';
import type { Instrument } from '@/types/schema/Instrument';
import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';
import { INSTRUMENT_CATALOG, getAvailableMarketSegments } from '@/data/instrumentCatalog';

/**
 * Props for the InstrumentSelectionModal component
 */
interface InstrumentSelectionModalProps {
  /** Whether the modal is open */
  isOpen: boolean;

  /** Handler for closing the modal */
  onClose: () => void;

  /** Handler for importing selected instruments */
  onImport: (instruments: InstrumentSelection[]) => void;

  /** List of instrument symbols that are already in the hierarchy */
  existingInstruments?: string[];

  /** Optional filtering context */
  filterContext?: {
    assetClass?: string;
    marketSegment?: string;
    subAssetClass?: string;
  } | undefined;
}

/**
 * Simplified instrument data for selection
 */
export interface InstrumentSelection {
  symbol: string;
  displayName: string;
  assetClass: AssetClassType;
  marketSegment: string;
  subAssetClass?: string | undefined;
  catalogId: string; // Reference to catalog entry
}

/**
 * Filter state for instrument selection
 */
interface FilterState {
  searchTerm: string;
  assetClassFilter: AssetClassType | 'all';
  marketSegmentFilter: string;
  subAssetClassFilter: string;
}

/**
 * Convert InstrumentCatalogEntry to InstrumentSelection format
 */
const catalogEntryToSelection = (catalogEntry: InstrumentCatalogEntry): InstrumentSelection => ({
  symbol: catalogEntry.symbol,
  displayName: catalogEntry.name,
  assetClass: catalogEntry.classification.assetClass as AssetClassType,
  marketSegment: catalogEntry.classification.marketSegment || 'other',
  subAssetClass: catalogEntry.classification.subAssetClass || undefined,
  catalogId: catalogEntry.id
});

/**
 * Asset class filter options
 */
const assetClassFilterOptions = [
  { key: 'all', label: 'All Asset Classes' },
  { key: 'futures' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('futures' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'forex' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('forex' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'stocks' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('stocks' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'crypto' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('crypto' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'options' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('options' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'cfd' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('cfd' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'bonds' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('bonds' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP) },
  { key: 'funds' as const satisfies AssetClassType, label: formatAssetClassTypeDisplay('funds' as const satisfies AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP) }
] as const;


/**
 * Helper function to validate if a market segment is valid for filtering
 */
const isValidMarketSegment = (marketSegment: string | undefined, availableSegments: { key: string; label: string; }[]): boolean => {
  if (!marketSegment) return false;
  return availableSegments.some(segment => segment.key === marketSegment);
};

/**
 * Helper to get asset class display color
 */
const getAssetClassColor = (assetClass: AssetClassType): 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
  const colorMap: Record<AssetClassType, 'primary' | 'secondary' | 'success' | 'warning' | 'danger'> = {
    'futures': 'primary',
    'forex': 'success',  
    'stocks': 'secondary',
    'crypto': 'warning',
    'options': 'danger',
    'cfd': 'primary',
    'bonds': 'secondary',
    'funds': 'success'
  };
  return colorMap[assetClass] || 'primary';
};

/**
 * InstrumentSelectionModal component for selecting instruments
 */
export const InstrumentSelectionModal: React.FC<InstrumentSelectionModalProps> = ({
  isOpen,
  onClose,
  onImport,
  existingInstruments = [],
  filterContext
}) => {
  const [instruments, setInstruments] = useState<InstrumentSelection[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<FilterState>(() => {
    return {
      searchTerm: '',
      assetClassFilter: (filterContext?.assetClass ? filterContext.assetClass : 'all') as AssetClassType | 'all',
      marketSegmentFilter: filterContext?.marketSegment ? filterContext.marketSegment : 'all',
      subAssetClassFilter: filterContext?.subAssetClass ? filterContext.subAssetClass : 'all'
    };
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Instrument creation modal state
  const { isOpen: isInstrumentModalOpen, onOpen: onInstrumentModalOpen, onClose: onInstrumentModalClose } = useDisclosure();

  // Reset filters when modal opens with new filterContext
  useEffect(() => {
    if (isOpen) {
      setFilters({
        searchTerm: '',
        assetClassFilter: (filterContext?.assetClass ? filterContext.assetClass : 'all') as AssetClassType | 'all',
        marketSegmentFilter: filterContext?.marketSegment ? filterContext.marketSegment : 'all',
        subAssetClassFilter: filterContext?.subAssetClass ? filterContext.subAssetClass : 'all'
      });
      setSelectedKeys(new Set()); // Clear selections
    }
  }, [isOpen, filterContext]);

  // Load instruments from catalog on mount
  useEffect(() => {
    const loadInstruments = async (): Promise<void> => {
      setIsLoading(true);
      try {
        // Convert catalog entries to selection format
        const catalogSelections = INSTRUMENT_CATALOG.map(catalogEntryToSelection);
        setInstruments(catalogSelections);
      } catch (error) {
        console.error('Failed to load instrument catalog:', error);
      } finally {
        setIsLoading(false);
      }
    };

    void loadInstruments();
  }, []);

  // Filter instruments based on current filter state
  const filteredInstruments = useMemo(() => {
    return instruments.filter(instrument => {
      // Search term filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch =
          instrument.symbol.toLowerCase().includes(searchLower) ||
          instrument.displayName.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Asset class filter
      if (filters.assetClassFilter !== 'all' && instrument.assetClass !== filters.assetClassFilter) {
        return false;
      }

      // Market segment filter
      if (filters.marketSegmentFilter !== 'all' && instrument.marketSegment !== filters.marketSegmentFilter) {
        return false;
      }

      // Sub-asset class filter
      if (filters.subAssetClassFilter !== 'all') {
        if (!instrument.subAssetClass && filters.subAssetClassFilter !== 'none') {
          return false;
        }
        if (instrument.subAssetClass && instrument.subAssetClass !== filters.subAssetClassFilter) {
          return false;
        }
      }

      return true;
    });
  }, [instruments, filters]);

  // Get unique market segments from catalog
  const availableMarketSegments = useMemo(() => {
    const segments = getAvailableMarketSegments();
    const segmentOptions = [
      { key: 'all', label: 'All Market Segments' },
      ...segments.map(segment => ({
        key: segment,
        label: segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' ')
      }))
    ];

    // Add current filter context segment if it's not already in the list (handles custom segments)
    if (filterContext?.marketSegment && !segmentOptions.some(option => option.key === filterContext.marketSegment)) {
      segmentOptions.push({
        key: filterContext.marketSegment,
        label: `${filterContext.marketSegment.charAt(0).toUpperCase() + filterContext.marketSegment.slice(1)} (Custom)`
      });
    }

    return segmentOptions;
  }, [filterContext?.marketSegment]);

  // Get unique sub-asset classes from filtered instruments
  const availableSubAssetClasses = useMemo(() => {
    const subAssetClasses = new Set<string>();

    // Only show sub-asset classes for the currently selected asset class and market segment
    const relevantInstruments = instruments.filter(instrument => {
      if (filters.assetClassFilter !== 'all' && instrument.assetClass !== filters.assetClassFilter) {
        return false;
      }
      if (filters.marketSegmentFilter !== 'all' && instrument.marketSegment !== filters.marketSegmentFilter) {
        return false;
      }
      return true;
    });

    relevantInstruments.forEach(instrument => {
      if (instrument.subAssetClass) {
        subAssetClasses.add(instrument.subAssetClass);
      }
    });

    const subAssetClassOptions = [
      { key: 'all', label: 'All Sub-Asset Classes' },
      { key: 'none', label: 'None (No Sub-Asset Class)' },
      ...Array.from(subAssetClasses).sort().map(subAssetClass => ({
        key: subAssetClass,
        label: subAssetClass.charAt(0).toUpperCase() + subAssetClass.slice(1).replace('-', ' ')
      }))
    ];

    // Add current filter context sub-asset class if it's not already in the list
    if (filterContext?.subAssetClass && !subAssetClassOptions.some(option => option.key === filterContext.subAssetClass)) {
      subAssetClassOptions.push({
        key: filterContext.subAssetClass,
        label: `${filterContext.subAssetClass.charAt(0).toUpperCase() + filterContext.subAssetClass.slice(1)} (Custom)`
      });
    }

    return subAssetClassOptions;
  }, [instruments, filters.assetClassFilter, filters.marketSegmentFilter, filterContext?.subAssetClass]);

  // Group instruments by asset class for sections
  const groupedInstruments = useMemo(() => {
    const groups = new Map<AssetClassType, InstrumentSelection[]>();

    filteredInstruments.forEach(instrument => {
      const assetClass = instrument.assetClass;
      if (!groups.has(assetClass)) {
        groups.set(assetClass, []);
      }
      const assetClassGroup = groups.get(assetClass);
      if (assetClassGroup) {
        assetClassGroup.push(instrument);
      }
    });

    // Sort groups by asset class name and sort instruments within each group
    const sortedGroups = Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([assetClass, instruments]) => ({
        assetClass,
        instruments: instruments.sort((a, b) => a.symbol.localeCompare(b.symbol))
      }));

    return sortedGroups;
  }, [filteredInstruments]);

  // Get selected instruments count
  const selectedCount = useMemo(() => {
    return selectedKeys.size;
  }, [selectedKeys]);

  // Check if we're filtering for custom market segments (not in catalog)
  const isCustomSegmentContext = useMemo(() => {
    const segments = getAvailableMarketSegments();
    const availableSegments = [
      { key: 'all', label: 'All Market Segments' },
      ...segments.map(segment => ({
        key: segment,
        label: segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' ')
      }))
    ];

    return filterContext?.marketSegment &&
           !isValidMarketSegment(filterContext.marketSegment, availableSegments);
  }, [filterContext]);

  // Determine context level and what should be locked
  const contextLevel = useMemo(() => {
    if (!filterContext) return 'none'; // From main screen - no locks

    if (filterContext.subAssetClass) return 'subAssetClass'; // Lock all filters
    if (filterContext.marketSegment) return 'marketSegment'; // Lock asset class + market segment
    if (filterContext.assetClass) return 'assetClass'; // Lock asset class only

    return 'none';
  }, [filterContext]);

  // Check if current context represents a custom (non-catalog) segment/sub-asset class
  const isCustomContext = useMemo(() => {
    if (contextLevel === 'marketSegment') {
      return isCustomSegmentContext;
    }
    if (contextLevel === 'subAssetClass') {
      // Check if the sub-asset class exists in any catalog instruments for the given context
      const hasInstrumentsWithSubAssetClass = instruments.some(instrument => {
        return instrument.assetClass === filterContext?.assetClass &&
               instrument.marketSegment === filterContext?.marketSegment &&
               instrument.subAssetClass === filterContext?.subAssetClass;
      });

      // If no instruments found with this exact combination, it's a custom context
      return !hasInstrumentsWithSubAssetClass;
    }
    return false;
  }, [contextLevel, isCustomSegmentContext, instruments, filterContext]);


  // Handle select all/none
  const handleSelectAll = (): void => {
    const hasSelected = filteredInstruments.some(instrument => selectedKeys.has(instrument.catalogId));
    const filteredIds = new Set(filteredInstruments.map(instrument => instrument.catalogId));
    
    if (hasSelected) {
      // Deselect all filtered instruments
      setSelectedKeys(prev => new Set([...prev].filter(key => !filteredIds.has(key))));
    } else {
      // Select all filtered instruments
      setSelectedKeys(prev => new Set([...prev, ...filteredIds]));
    }
  };

  // Update filter state
  const updateFilter = (field: keyof FilterState, value: string | AssetClassType): void => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Handle import action
  const handleImport = (): void => {
    const selectedInstruments = instruments.filter(instrument => selectedKeys.has(instrument.catalogId));
    console.log('🚀 STEP 4: Instrument import with real data', selectedInstruments);
    setIsSubmitting(true);
    
    // Call the onImport handler with selected instruments
    try {
      onImport(selectedInstruments);
      onClose();
      // Reset selections
      setSelectedKeys(new Set());
      setFilters({ searchTerm: '', assetClassFilter: 'all', marketSegmentFilter: 'all', subAssetClassFilter: 'all' });
    } catch (error) {
      console.error('Failed to import instruments:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel action
  const handleCancel = (): void => {
    // Reset selections and filters
    setSelectedKeys(new Set());
    setFilters({ searchTerm: '', assetClassFilter: 'all', marketSegmentFilter: 'all', subAssetClassFilter: 'all' });
    onClose();
  };

  // Handle custom instrument creation
  const handleCreateInstrument = (): void => {
    console.log('🚀 STEP 4: Create custom instrument clicked');
    onInstrumentModalOpen();
  };

  // Handle saving new instrument from InstrumentModal
  const handleInstrumentSave = (instrument: Instrument): void => {
    console.log('🚀 STEP 4: New instrument saved', instrument);
    
    // Convert Instrument to InstrumentSelection and add to list
    const newInstrumentSelection: InstrumentSelection = {
      symbol: instrument.symbol,
      displayName: instrument.displayName,
      assetClass: instrument.classification.assetClass,
      marketSegment: instrument.classification.marketSegment || 'other',
      subAssetClass: instrument.classification.subAssetClass || undefined,
      catalogId: `custom-${instrument.id}` // Mark as custom instrument
    };
    
    setInstruments(prev => [...prev, newInstrumentSelection]);
    // Auto-select newly created instrument
    setSelectedKeys(prev => new Set([...prev, newInstrumentSelection.catalogId]));
    onInstrumentModalClose();
  };

  // Handle instrument creation errors
  const handleInstrumentError = (error: string): void => {
    console.error('🚀 STEP 4: Instrument creation error', error);
    // TODO: Show error toast or modal in future enhancement
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="2xl"
      classNames={{
        base: "max-h-[100vh] sm:max-h-[90vh] h-full sm:h-auto",
        wrapper: "items-end sm:items-center p-0 sm:p-6",
        backdrop: "bg-overlay/50"
      }}
      scrollBehavior="inside"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1 pb-0 px-4 sm:px-6 pt-4 sm:pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold">Select Instruments</h3>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    color="secondary"
                    variant="bordered"
                    startContent={<Plus className="w-3 h-3" />}
                    onPress={handleCreateInstrument}
                    className='mr-4'
                  >
                    <span className="hidden sm:inline">Create New</span>
                    <span className="sm:hidden">New</span>
                  </Button>
                  {selectedCount > 0 && (
                    <Chip color="primary" size="sm" variant="flat">
                      {selectedCount} selected
                    </Chip>
                  )}
                </div>
              </div>
              <p className="text-sm text-text-secondary font-normal">
                Choose instruments to add to your fee configuration
              </p>

              {/* Fixed Search and Filters Section */}
              {!isLoading && (
                <div className="space-y-4 pt-4 border-t border-default-200">
                  <Input
                    placeholder="Search instruments by symbol or name..."
                    value={filters.searchTerm}
                    onValueChange={(value) => updateFilter('searchTerm', value)}
                    startContent={<Search className="w-4 h-4 text-text-secondary" />}
                    className="w-full"
                  />

                  {/* Desktop: Show filters inline */}
                  <div className="hidden sm:block">
                    <div className="grid grid-cols-3 gap-4">
                      <Select
                        label="Asset Class"
                        variant='underlined'
                        size="sm"
                        selectedKeys={[filters.assetClassFilter]}
                        onSelectionChange={(keys) => {
                          updateFilter('assetClassFilter', Array.from(keys)[0] as string | AssetClassType);
                          updateFilter('marketSegmentFilter', 'all'); // Reset market segment
                          updateFilter('subAssetClassFilter', 'all'); // Reset sub-asset class
                        }}
                        isDisabled={contextLevel !== 'none'}
                        aria-label="Filter instruments by asset class"
                        classNames={{
                          listboxWrapper: "data-[hover=true]:bg-primary-50 data-[selectable=true]:focus:bg-primary-50 [&_[data-selected=true]]:!bg-primary-50 [&_[aria-selected=true]]:!bg-primary-50 [&_[data-selected=false]]:!bg-transparent [&_[aria-selected=false]]:!bg-transparent"
                        }}
                      >
                        {assetClassFilterOptions.map((item) => (
                          <SelectItem key={item.key}>{item.label}</SelectItem>
                        ))}
                      </Select>

                      <Select
                        label="Market Segment"
                        variant="underlined"
                        size="sm"
                        selectedKeys={[filters.marketSegmentFilter]}
                        onSelectionChange={(keys) => {
                          updateFilter('marketSegmentFilter', Array.from(keys)[0] as string);
                          updateFilter('subAssetClassFilter', 'all'); // Reset sub-asset class when market segment changes
                        }}
                        isDisabled={contextLevel === 'marketSegment' || contextLevel === 'subAssetClass'}
                        aria-label="Filter instruments by market segment"
                        classNames={{
                          listboxWrapper: "data-[hover=true]:bg-primary-50 data-[selectable=true]:focus:bg-primary-50 [&_[data-selected=true]]:!bg-primary-50 [&_[aria-selected=true]]:!bg-primary-50 [&_[data-selected=false]]:!bg-transparent [&_[aria-selected=false]]:!bg-transparent"
                        }}
                      >
                        {availableMarketSegments.map((item) => (
                          <SelectItem key={item.key}>{item.label}</SelectItem>
                        ))}
                      </Select>

                      <Select
                        label="Sub-Asset Class"
                        variant="underlined"
                        size="sm"
                        selectedKeys={[filters.subAssetClassFilter]}
                        onSelectionChange={(keys) => updateFilter('subAssetClassFilter', Array.from(keys)[0] as string)}
                        isDisabled={contextLevel === 'subAssetClass'}
                        aria-label="Filter instruments by sub-asset class"
                        classNames={{
                          listboxWrapper: "data-[hover=true]:bg-primary-50 data-[selectable=true]:focus:bg-primary-50 [&_[data-selected=true]]:!bg-primary-50 [&_[aria-selected=true]]:!bg-primary-50 [&_[data-selected=false]]:!bg-transparent [&_[aria-selected=false]]:!bg-transparent"
                        }}
                      >
                        {availableSubAssetClasses.map((item) => (
                          <SelectItem key={item.key}>{item.label}</SelectItem>
                        ))}
                      </Select>
                    </div>
                  </div>

                  {/* Mobile: Collapsible filters in Accordion */}
                  <div className="sm:hidden">
                    <Accordion variant="light" isCompact>
                      <AccordionItem
                        key="filters"
                        aria-label="Filter options"
                        title={`Filters ${filters.assetClassFilter !== 'all' || filters.marketSegmentFilter !== 'all' || filters.subAssetClassFilter !== 'all' ? '(Active)' : ''}`}
                        classNames={{
                          title: "text-sm font-medium",
                          trigger: "py-2",
                          content: "pt-0 pb-2"
                        }}
                      >
                        <div className="space-y-3">
                          <Select
                            label="Asset Class"
                            variant='bordered'
                            size="sm"
                            selectedKeys={[filters.assetClassFilter]}
                            onSelectionChange={(keys) => {
                              updateFilter('assetClassFilter', Array.from(keys)[0] as string | AssetClassType);
                              updateFilter('marketSegmentFilter', 'all'); // Reset market segment
                              updateFilter('subAssetClassFilter', 'all'); // Reset sub-asset class
                            }}
                            isDisabled={contextLevel !== 'none'}
                            aria-label="Filter instruments by asset class"
                          >
                            {assetClassFilterOptions.map((item) => (
                              <SelectItem key={item.key}>{item.label}</SelectItem>
                            ))}
                          </Select>

                          <Select
                            label="Market Segment"
                            variant="bordered"
                            size="sm"
                            selectedKeys={[filters.marketSegmentFilter]}
                            onSelectionChange={(keys) => {
                              updateFilter('marketSegmentFilter', Array.from(keys)[0] as string);
                              updateFilter('subAssetClassFilter', 'all'); // Reset sub-asset class when market segment changes
                            }}
                            isDisabled={contextLevel === 'marketSegment' || contextLevel === 'subAssetClass'}
                            aria-label="Filter instruments by market segment"
                          >
                            {availableMarketSegments.map((item) => (
                              <SelectItem key={item.key}>{item.label}</SelectItem>
                            ))}
                          </Select>

                          <Select
                            label="Sub-Asset Class"
                            variant="bordered"
                            size="sm"
                            selectedKeys={[filters.subAssetClassFilter]}
                            onSelectionChange={(keys) => updateFilter('subAssetClassFilter', Array.from(keys)[0] as string)}
                            isDisabled={contextLevel === 'subAssetClass'}
                            aria-label="Filter instruments by sub-asset class"
                          >
                            {availableSubAssetClasses.map((item) => (
                              <SelectItem key={item.key}>{item.label}</SelectItem>
                            ))}
                          </Select>
                        </div>
                      </AccordionItem>
                    </Accordion>
                  </div>

                  {/* Bulk Selection */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-text-secondary">
                      {filteredInstruments.length} instrument{filteredInstruments.length !== 1 ? 's' : ''} found
                    </p>
                    <Button
                      size="sm"
                      variant="light"
                      onPress={handleSelectAll}
                      startContent={<CheckSquare className="w-3 h-3" />}
                    >
                      {filteredInstruments.some(instrument => selectedKeys.has(instrument.catalogId)) ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                </div>
              )}
            </ModalHeader>

            <ModalBody className="pt-4 px-4 sm:px-6 pb-4 sm:pb-6 flex-1 min-h-0">
              {isLoading ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-default-300 mx-auto mb-4 animate-pulse" />
                  <h4 className="text-large font-medium text-default-500 mb-2">Loading Instruments</h4>
                  <p className="text-small text-default-400">
                    Please wait while we load the instrument catalog...
                  </p>
                </div>
              ) : (
                <>
                  {/* Scrollable Instrument List */}
                  <div className="flex-1 min-h-0">
                {isCustomContext ? (
                  // Custom segments/sub-asset classes: Always show Create New screen
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-default-300 mx-auto mb-4" />
                    <h4 className="text-large font-medium text-default-500 mb-2">No Catalog Instruments Available</h4>
                    <p className="text-small text-default-400 mb-4">
                      {contextLevel === 'marketSegment'
                        ? `Custom market segment "${filterContext?.marketSegment}" doesn't have pre-existing catalog instruments.`
                        : `Custom sub-asset class "${filterContext?.subAssetClass}" doesn't have pre-existing catalog instruments.`
                      }
                      <br />Use "Create New" to add a custom instrument for this {contextLevel === 'marketSegment' ? 'segment' : 'sub-asset class'}.
                    </p>
                    <Button
                      color="primary"
                      variant="flat"
                      size="sm"
                      startContent={<Plus className="w-4 h-4" />}
                      onPress={handleCreateInstrument}
                    >
                      Create New Instrument
                    </Button>
                  </div>
                ) : groupedInstruments.length > 0 ? (
                  <Listbox
                    aria-label="Select instruments"
                    selectionMode="multiple"
                    selectedKeys={selectedKeys}
                    onSelectionChange={(keys) => {
                      setSelectedKeys(new Set(Array.from(keys) as string[]));
                    }}
                    className="w-full [&_[data-selected=true]]:!bg-transparent [&_[aria-selected=true]]:!bg-transparent [&_[data-selected=false]]:!bg-transparent [&_[aria-selected=false]]:!bg-transparent"
                  >
                    {groupedInstruments.map((group, groupIndex) => (
                      <ListboxSection
                        key={group.assetClass}
                        title={formatAssetClassTypeDisplay(group.assetClass, ASSET_CLASS_TYPE_DISPLAY_MAP)}
                        showDivider={groupIndex < groupedInstruments.length - 1}
                        classNames={{
                          heading: "text-tiny font-semibold text-default-500 uppercase tracking-wide",
                          group: "divide-y divide-default-100"
                        }}
                      >
                        {group.instruments.map((instrument) => {
                          const isAlreadyAdded = existingInstruments.includes(instrument.symbol);
                          return (
                            <ListboxItem
                              key={instrument.catalogId}
                              className={`py-3 ${isAlreadyAdded ? 'opacity-60' : ''}`}
                              textValue={`${instrument.symbol} ${instrument.displayName}`}
                              isDisabled={isAlreadyAdded}
                              startContent={
                                <div
                                  className={`w-8 h-8 rounded-full bg-${getAssetClassColor(instrument.assetClass)} text-white flex items-center justify-center text-xs font-semibold ${isAlreadyAdded ? 'opacity-50' : ''}`}
                                >
                                  {instrument.symbol.substring(0, 2)}
                                </div>
                              }
                            >
                            <div className="flex justify-between items-start w-full">
                              {/* Left column: Symbol and description */}
                              <div className="flex flex-col flex-1 min-w-0">
                                <span className="text-small font-semibold">{instrument.symbol}</span>
                                <span className="text-tiny text-default-400 truncate">{instrument.displayName}</span>
                                {isAlreadyAdded && (
                                  <span className="text-tiny text-warning-500 font-medium mt-1">Already added</span>
                                )}
                              </div>

                              {/* Right column: Market info (no asset class since it's in section title) */}
                              <div className="flex flex-col items-end text-right ml-4 flex-shrink-0">
                                <div className="text-tiny text-default-500 font-medium">
                                  <span className="capitalize">{instrument.marketSegment.replace('-', ' ')}</span>
                                </div>
                                {instrument.subAssetClass && (
                                  <div className="text-tiny text-default-400">
                                    <span className="capitalize">{instrument.subAssetClass.replace('-', ' ')}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </ListboxItem>
                          );
                        })}
                      </ListboxSection>
                    ))}
                  </Listbox>
                ) : (
                  <div className="text-center py-8">
                    <Package className="w-12 h-12 text-default-300 mx-auto mb-4" />
                    <h4 className="text-large font-medium text-default-500 mb-2">No Instruments Found</h4>
                    <p className="text-small text-default-400">
                      Try adjusting your search terms or filters
                    </p>
                  </div>
                )}
                  </div>
                </>
              )}
            </ModalBody>

            <ModalFooter className="px-4 sm:px-6 py-4 sm:py-6">
              <Button 
                color="danger" 
                variant="light" 
                onPress={handleCancel}
                isDisabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                color="primary" 
                onPress={handleImport}
                isLoading={isSubmitting}
                isDisabled={selectedCount === 0}
                startContent={!isSubmitting ? <Plus className="w-4 h-4" /> : undefined}
              >
                {isSubmitting ? 'Importing...' : `Import ${selectedCount} Instrument${selectedCount !== 1 ? 's' : ''}`}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>

      {/* Instrument Creation Modal */}
      <InstrumentModal
        isOpen={isInstrumentModalOpen}
        onClose={onInstrumentModalClose}
        onSave={handleInstrumentSave}
        onError={handleInstrumentError}
{...(filterContext && {
          initialContext: {
            ...(filterContext.assetClass && { assetClass: filterContext.assetClass as AssetClassType }),
            ...(filterContext.marketSegment && { marketSegment: filterContext.marketSegment as MarketSegment }),
            ...(filterContext.subAssetClass && { subAssetClass: filterContext.subAssetClass as SubAssetClass })
          }
        })}
      />
    </Modal>
  );
};